import React from 'react';
import { cn } from '../lib/utils';

/**
 * Sparkline — tiny line chart without axes, used inline in cards.
 *
 * props:
 *  data: number[]
 *  height, width (defaults to filling the container)
 *  color: CSS color for the line/fill
 *  showDot: bool — show the last data point as a dot
 *  trend: 'up' | 'down' | 'neutral' — uses success/destructive/muted
 */
export function Sparkline({
  data = [],
  height = 40,
  width,           // optional; defaults to container
  color,
  showDot = true,
  trend,
  fillOpacity = 0.15,
  className,
}) {
  if (!data.length) return <div style={{ height }} />;

  const trendColor =
    trend === 'up' ? 'hsl(var(--success))' :
    trend === 'down' ? 'hsl(var(--destructive))' :
    color || 'hsl(var(--brand))';

  const strokeColor = color || trendColor;
  const fillColor = trendColor;
  const strokeWidth = 1.5;

  // viewBox: 0..(len-1) x 0..max with padding
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const padY = 4;
  const usable = height - padY * 2;
  const stepX = 1;
  const len = data.length;

  // Build the SVG path
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = padY + usable - ((v - min) / range) * usable;
    return [x, y];
  });

  const linePath = points
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(' ');

  const areaPath = `${linePath} L ${(len - 1) * stepX} ${height} L 0 ${height} Z`;

  const last = points[points.length - 1];

  return (
    <svg
      viewBox={`-${strokeWidth} 0 ${len + strokeWidth * 2} ${height}`}
      preserveAspectRatio="none"
      width={width || '100%'}
      height={height}
      className={cn('overflow-visible', className)}
    >
      <defs>
        <linearGradient id={`sparkfill-${data.length}-${Math.random().toString(36).slice(2)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillColor} stopOpacity={fillOpacity} />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#sparkfill-${data.length}-${data.join('')})`} />
      <path
        d={linePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showDot && (
        <>
          <circle
            cx={last[0]}
            cy={last[1]}
            r={4}
            fill={strokeColor}
            opacity="0.25"
          />
          <circle
            cx={last[0]}
            cy={last[1]}
            r={2}
            fill={strokeColor}
          />
        </>
      )}
    </svg>
  );
}